/**
 * Theme applier — reads a colour palette and writes the corresponding
 * CSS custom properties to `document.documentElement` so the entire site
 * rebrands at runtime. Derived variants (soft, hover, deep) are computed
 * automatically so a client only has to provide six base colours.
 *
 * Base palette keys (all optional — missing keys keep the CSS defaults):
 *   ink     — body text + dark footer
 *   cream   — alternating section background
 *   indigo  — primary brand
 *   gold    — accent / CTA fill
 *   rescue  — rescue section background
 *   surface — card border / hover state
 */

const hexToRgb = (hex) => {
  const clean = String(hex).replace("#", "");
  const v =
    clean.length === 3
      ? clean.split("").map((c) => c + c).join("")
      : clean;
  const num = parseInt(v, 16);
  return { r: (num >> 16) & 0xff, g: (num >> 8) & 0xff, b: num & 0xff };
};

const rgba = (hex, alpha) => {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const darken = (hex, amount = 0.1) => {
  const { r, g, b } = hexToRgb(hex);
  const k = 1 - amount;
  const to2 = (n) => Math.max(0, Math.min(255, Math.round(n * k))).toString(16).padStart(2, "0");
  return `#${to2(r)}${to2(g)}${to2(b)}`;
};

export function applyTheme(colors = {}) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;

  // Base tokens
  if (colors.ink)     root.style.setProperty("--pp-ink", colors.ink);
  if (colors.cream)   root.style.setProperty("--pp-cream", colors.cream);
  if (colors.indigo)  root.style.setProperty("--pp-indigo", colors.indigo);
  if (colors.gold)    root.style.setProperty("--pp-gold", colors.gold);
  if (colors.rescue)  root.style.setProperty("--pp-rescue", colors.rescue);
  if (colors.surface) root.style.setProperty("--pp-surface", colors.surface);

  // Derived tokens — computed when the client supplies the base colour.
  if (colors.indigo) {
    root.style.setProperty("--pp-indigo-soft", rgba(colors.indigo, 0.08));
    root.style.setProperty("--pp-indigo-medium", rgba(colors.indigo, 0.18));
  }
  if (colors.gold) {
    root.style.setProperty("--pp-gold-hover", colors.goldHover || darken(colors.gold, 0.08));
  }
  if (colors.rescue) {
    root.style.setProperty("--pp-rescue-deep", colors.rescueDeep || darken(colors.rescue, 0.12));
  }
  if (colors.cream) {
    // Cream-soft is a slightly deeper cream (used for hover bands + accordion).
    root.style.setProperty("--pp-cream-soft", colors.creamSoft || darken(colors.cream, 0.04));
  }
}
